import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { zodFlavorId } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, meta } from "./meta.mjs";
import { ReferralProgramStatusEnum } from "./types.mjs";
import { z as z$1 } from "zod";

// src/models/sponsorInfo.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date());
const latestSponsorshipStatusSchema = z$1.enum(["active", "expired"]);
const sponsorshipListItemSchema = z$1.object({
	id: z$1.string(),
	referralProgramId: zodFlavorId(),
	referralProgramName: z$1.string(),
	consultationIncluded: z$1.boolean(),
	status: z$1.enum(Object.values(ReferralProgramStatusEnum)),
	startDate: firestoreDate.optional(),
	endDate: firestoreDate.optional()
}).superRefine((item, ctx) => {
	if (item.status === "eligible") return;
	if (!item.startDate) ctx.addIssue({
		code: "custom",
		message: "startDate is required unless status is eligible",
		path: ["startDate"]
	});
	if (!item.endDate) ctx.addIssue({
		code: "custom",
		message: "endDate is required unless status is eligible",
		path: ["endDate"]
	});
});
const sponsorInfoSchema = z$1.object({
	id: zodFlavorId(),
	customerId: zodFlavorId(),
	referralProgramId: zodFlavorId().optional(),
	startDate: firestoreDate.optional(),
	endDate: firestoreDate.optional(),
	referralProgramName: z$1.string().optional(),
	consultationIncluded: z$1.boolean().optional(),
	status: latestSponsorshipStatusSchema.optional(),
	editedBy: meta(z$1.string().optional(), { pii: { category: "identifier" } }),
	editedAt: firestoreDate.optional(),
	sponsorshipList: meta(z$1.array(sponsorshipListItemSchema).optional(), { excludeFromBqSchema: true })
}).superRefine((doc, ctx) => {
	if (!(doc.referralProgramId != null || doc.status != null || doc.startDate != null || doc.endDate != null || doc.referralProgramName != null || doc.consultationIncluded != null)) return;
	if (!doc.referralProgramId) ctx.addIssue({
		code: "custom",
		message: "referralProgramId is required on the latest sponsorship",
		path: ["referralProgramId"]
	});
	if (!doc.status) ctx.addIssue({
		code: "custom",
		message: "status is required on the latest sponsorship",
		path: ["status"]
	});
	if (!doc.startDate) ctx.addIssue({
		code: "custom",
		message: "startDate is required on the latest sponsorship",
		path: ["startDate"]
	});
	if (!doc.endDate) ctx.addIssue({
		code: "custom",
		message: "endDate is required on the latest sponsorship",
		path: ["endDate"]
	});
	if (doc.referralProgramName == null) ctx.addIssue({
		code: "custom",
		message: "referralProgramName is required on the latest sponsorship",
		path: ["referralProgramName"]
	});
	if (doc.consultationIncluded == null) ctx.addIssue({
		code: "custom",
		message: "consultationIncluded is required on the latest sponsorship",
		path: ["consultationIncluded"]
	});
});
/** List entry with status `active`, or the legacy top-level row when no list is stored. */
function selectActiveSponsorship(doc) {
	if (!doc) return null;
	const list = doc.sponsorshipList;
	if (list && list.length > 0) {
		const active = list.find((item) => item.status === "active");
		if (!active?.startDate || !active.endDate) return null;
		return {
			...doc,
			referralProgramId: active.referralProgramId,
			referralProgramName: active.referralProgramName,
			consultationIncluded: active.consultationIncluded,
			status: "active",
			startDate: active.startDate,
			endDate: active.endDate
		};
	}
	if (doc.status === "active" && doc.startDate && doc.endDate && doc.referralProgramId && doc.referralProgramName != null && doc.consultationIncluded != null) return doc;
	return null;
}
/**
* Sponsor infos collection definition
* Path: sponsor_infos/{customerId}
*/
const SponsorInfos = defineFirestoreCollection("sponsor_infos", sponsorInfoSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "sponsor_infos"
});

export { SponsorInfos, selectActiveSponsorship, sponsorInfoSchema, sponsorshipListItemSchema };
//# sourceMappingURL=sponsorInfo.mjs.map